import { ProjectResource, usePostProjectsByProjectDuplicateMutation } from '@/api/serverApi'
import { Button, InputLabel, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const duplicateProjectSchema = v.object({
  name: v.string([v.minLength(1, 'Name is required')]),
})

type DuplicateProjectFormInputs = v.Input<typeof duplicateProjectSchema>

type PropTypes = {
  project: ProjectResource,
  onCompleted?: (project: ProjectResource) => void,
  onClose?: () => void,
}

function DuplicateProjectForm ({ project, onCompleted, onClose }: PropTypes) {
  const textSpec = {
    projectNameField: {
      label: 'Name',
      placeholder: 'Enter name',
    },
    buttonLabels: {
      cancel: 'Cancel',
      create: 'Duplicate Project',
    },
    successToastMessage: {
      title: 'Project duplicated',
      body: 'Your duplicate project has been created successfully.',
    },
    errorToastMessage: {
      title: 'Something went wrong',
      body: 'Please try again later.',
    },
  }

  const errorHandler = useErrorHandler()

  const { register, handleSubmit, formState: { errors }, setError, reset } = useForm<DuplicateProjectFormInputs>({
    resolver: valibotResolver(duplicateProjectSchema),
  })

  const [duplicate, { isLoading: isSubmitting }] = usePostProjectsByProjectDuplicateMutation()

  const onSubmit: SubmitHandler<DuplicateProjectFormInputs> = (data) => {
    duplicate({
      project: project.id,
      body: data,
    })
      .unwrap()
      .then((response) => {
        onCompleted?.(response.data)
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
        reset()
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-start w-full max-w-[615px]"
    >
      <div className="my-[17px] w-full flex flex-col gap-[30px]">
        {/* Name Field */}
        <div>
          <InputLabel>{textSpec.projectNameField.label}</InputLabel>
          <TextInput
            placeholder={textSpec.projectNameField.placeholder}
            error={errors?.name?.message}
            {...register('name')}
          />
        </div>
      </div>

      <div className="w-full flex flex-row justify-end gap-2.5 mt-4">
        <Button
          key="close"
          variant="secondary"
          isLoading={isSubmitting}
          onClick={onClose}
        >
          {textSpec.buttonLabels.cancel}
        </Button>
        <Button
          key="create"
          variant="primary"
          isLoading={isSubmitting}
          type="submit"
        >
          {textSpec.buttonLabels.create}
        </Button>
      </div>
    </form>
  )
}

export default DuplicateProjectForm
